// Modal de detalle completo de engagement de un comprador

import React, { useState, useEffect } from 'react';
import { X, TrendingUp, Eye, Clock, Video, Image, FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { getInviteEngagementEvents } from '../../../services/supabase/engagement';
import { BiddingAttemptTimeline } from './BiddingAttemptTimeline';
import { EventTimeline } from './EventTimeline';
import { getTimeInfo } from '../../../utils/engagementTimeFormatters';

/**
 * Modal de detalle completo de un comprador
 * Muestra timeline, intentos de puja y recomendaciones
 */
export const BuyerDetailModal = ({ buyer, propertyId, onClose }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFullTimeline, setShowFullTimeline] = useState(false);

  useEffect(() => {
    loadDetailedEvents();
  }, []);

  const loadDetailedEvents = async () => {
    try {
      setLoading(true);
      const allEvents = await getInviteEngagementEvents(propertyId, buyer.token);
      setEvents(allEvents);
    } catch (error) {
      console.error('Error cargando eventos detallados:', error);
    } finally {
      setLoading(false);
    }
  };

  // Analizar eventos para métricas detalladas
  const videoViewed = events.some(e => e.event_type === 'video_view_start');

  // Fotos: usar el summary consolidado
  const photoSummary = events.find(e => e.event_type === 'photo_session_summary');
  const photoViews = photoSummary?.event_data?.photosViewed || 0;
  const totalPhotos = photoSummary?.event_data?.totalPhotos || 0;

  // Documentos vistos (deduplicados por nombre)
  const documentEvents = events.filter(e => e.event_type === 'document_view_start');
  const documentsViewed = [...new Set(documentEvents.map(e => e.event_data?.documentName).filter(Boolean))];

  // Pestañas vistas (deduplicadas, en orden de primera visita)
  const tabsViewed = [...new Set(
    events
      .filter(e => e.event_type === 'tab_view')
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      .map(e => e.event_data?.tabName)
      .filter(Boolean)
  )];

  // Extraer intentos de puja
  const biddingEvents = events.filter(e => 
    e.event_type.startsWith('bid_')
  );

  // Helper: Formatear tiempo
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h`;
  };

  // Helper: Color del score (5 franjas)
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-400';
    if (score >= 60) return 'text-red-400';
    if (score >= 40) return 'text-amber-400';
    if (score >= 20) return 'text-blue-400';
    return 'text-slate-400';
  };

  return (
    <div 
      className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <div 
        className="bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-slate-700"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-slate-800 border-b border-slate-700 p-6 z-10">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <button
                  onClick={onClose}
                  className="text-slate-400 hover:text-white transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
                <div>
                  <h2 className="text-2xl font-bold text-white">{buyer.name}</h2>
                </div>
              </div>

              {/* Score prominente */}
              <div className="flex items-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className={`w-5 h-5 ${getScoreColor(buyer.score)}`} />
                  <span className="text-sm text-slate-400">Engagement Score:</span>
                </div>
                <div className={`text-4xl font-bold ${getScoreColor(buyer.score)}`}>
                  {buyer.score}
                  <span className="text-xl text-slate-500">/100</span>
                </div>
                
                {buyer.hasBid && (
                  <span className="ml-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 border border-green-500/50 text-green-400">
                    <CheckCircle className="w-3 h-3" />
                    Pujó
                  </span>
                )}
                
                {buyer.isStuck && !buyer.hasBid && (
                  <span className="ml-4 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-500/20 border border-amber-500/50 text-amber-400">
                    <AlertCircle className="w-3 h-3" />
                    Bloqueado
                  </span>
                )}
              </div>

              {/* Barra de progreso */}
              <div className="mt-3">
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${
                      buyer.score >= 85 ? 'bg-green-500' :
                      buyer.score >= 60 ? 'bg-red-500' :
                      buyer.score >= 40 ? 'bg-amber-500' :
                      buyer.score >= 20 ? 'bg-blue-500' : 'bg-slate-500'
                    }`}
                    style={{ width: `${buyer.score}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contenido */}
        <div className="p-6 space-y-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-4"></div>
              <p className="text-slate-400">Cargando detalles...</p>
            </div>
          ) : (
            <>
              {/* Métricas de actividad */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-amber-400" />
                  Actividad General
                </h3>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Visitas */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-slate-400 text-sm mb-1">Visitas</div>
                    <div className="text-2xl font-bold text-white">{buyer.visits}</div>
                    {buyer.lastVisit && (() => {
                      const timeInfo = getTimeInfo(buyer.lastVisit);
                      return (
                        <div className={`text-xs mt-1 ${timeInfo.colorClass}`}>
                          {timeInfo.timeAgo} <span className="text-slate-500">({timeInfo.shortDate})</span>
                        </div>
                      );
                    })()}
                  </div>

                  {/* Tiempo total: OCULTO - No confiable sin control de visibilidad */}
                  {/*
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-slate-400 text-sm mb-1">Tiempo total</div>
                    <div className="text-2xl font-bold text-white">
                      {formatTime(buyer.totalTime)}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {buyer.visits > 0 ? `${formatTime(Math.round(buyer.totalTime / buyer.visits))} por visita` : '-'}
                    </div>
                  </div>
                  */}

                  {/* Video */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-slate-400 text-sm mb-1 flex items-center gap-1">
                      <Video className="w-3 h-3" />
                      Video
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {videoViewed ? '▶️' : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {videoViewed ? 'Visto' : 'No visto'}
                    </div>
                  </div>

                  {/* Fotos */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-slate-400 text-sm mb-1 flex items-center gap-1">
                      <Image className="w-3 h-3" />
                      Fotos
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {photoViews > 0 ? `${photoViews}${totalPhotos > 0 ? `/${totalPhotos}` : ''}` : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {photoViews > 0 ? 'vistas' : 'No vistas'}
                    </div>
                  </div>

                  {/* Documentos */}
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-slate-400 text-sm mb-1 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Documentos
                    </div>
                    <div className="text-2xl font-bold text-white">
                      {documentsViewed.length > 0 ? documentsViewed.length : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {documentsViewed.length > 0 ? 'abiertos' : 'No abiertos'}
                    </div>
                  </div>
                </div>

                {/* Descripción leída */}
                {/* Lista de documentos vistos */}
                {documentsViewed.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm text-slate-400 mb-2 flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      Documentos abiertos:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {documentsViewed.map((name, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-slate-700/50 border border-slate-600/50 text-slate-300"
                        >
                          <FileText className="w-3 h-3 text-amber-400" />
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pestañas visitadas */}
                {tabsViewed.length > 0 && (
                  <div className="mt-4">
                    <div className="text-sm text-slate-400 mb-2 flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      Pestañas vistas:
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {tabsViewed.map((name, i) => (
                        <span
                          key={i}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs bg-blue-500/10 border border-blue-500/20 text-blue-300"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Intentos de puja */}
              {biddingEvents.length > 0 && (
                <BiddingAttemptTimeline 
                  events={biddingEvents}
                  buyer={buyer}
                />
              )}

              {/* Timeline completo */}
              <div className="bg-slate-900/50 rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Clock className="w-5 h-5 text-amber-400" />
                    Timeline Completo
                    <span className="text-slate-400 text-sm font-normal">
                      ({events.length} eventos)
                    </span>
                  </h3>
                  
                  <button
                    onClick={() => setShowFullTimeline(!showFullTimeline)}
                    className="text-sm text-amber-400 hover:text-amber-300"
                  >
                    {showFullTimeline ? 'Ocultar' : 'Ver todos'}
                  </button>
                </div>

                {showFullTimeline && (
                  <EventTimeline events={events} />
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyerDetailModal;